import CloseIcon from "@mui/icons-material/Close";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Collapse,
  Container,
  Grid,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import React, { useState } from "react";
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import AddNoteIcon from "src/icons/AddNoteIcon";
import api from "src/store/features/api/index";
import SecondaryButton from "../wallets/Modal/deposit/SecondaryButton";
import { createDataMobile, iconSelector } from "./helperfns";
import { TransactionDetailsStyle } from "./styles/TransactionDetailsStyle.styles";
import { addTransactionNote, useTransactionSlice } from "src/store/features/transactions.slice";
import { useAppDispatch } from "src/store/store";

interface TransactionDetailsProps {
  toggleDetails: any;
  details: ReturnType<typeof createDataMobile>;
  showTransactionDetails: boolean;
  date?: string;
}

export default function TransactionDetails(props: TransactionDetailsProps) {
  const { details, date, showTransactionDetails, toggleDetails } = props;
  const { notes } = useTransactionSlice();
  const dispatch = useAppDispatch();
  const classes = TransactionDetailsStyle();
  const [postNote] = api.useAddNoteToCtMutation();
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [addNewNote, setAddNewNote] = useState(details.note);
  const [displayNote, setDisplayNote] = useState<boolean>(false);
  const hasNote = Boolean(details.note) ?? false;
  const [saveNote, setSaveNote] = React.useState<boolean>(false);

  React.useEffect(() => {
    notes.forEach((note) => {
      if (note.id === details.id) {
        setAddNewNote(note.note); // set the note in the row
      }
    });
  }, [notes]);

  const handleNote = async () => {
    if (displayNote && addNewNote) {
      const maxNoteLength = 500;
      let newNote;

      if (addNewNote.length > maxNoteLength) {
        newNote = details.note ?? "";
      } else {
        newNote = addNewNote;
      }

      let note = {
        ctNoteDto: {
          ctId: details.id,
          note: newNote,
        },
      };
      try {
        await postNote(note);
        setNotificationOpen(true);
        setTimeout(() => {
          handleNotificationClose();
        }, 5000);
        setSaveNote(false);
        dispatch(addTransactionNote({ id: details.id, note: addNewNote }));
      } catch (e) {
        console.warn(e);
      }
    }

    setDisplayNote((prev): boolean => !prev);
  };

  const handleNotificationClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }

    setNotificationOpen(false);
  };

  const renderLines = () => {
    const lines = details?.lines;
    let renderResult = [];
    if (lines) {
      for (let i = 0; i < lines.length; i++) {
        const imgSrc = lines[i].base.logoUrl ? lines[i].base.logoUrl : lines[i].base.code;
        const currency = lines[i].base.name;
        const side = lines[i].side;
        const units = lines[i].units;
        const price = lines[i].price;
        const value = lines[i].value;
        const fee = lines[i].fee;

        renderResult.push(
          <div key={"accordion" + i}>
            <Accordion square expanded className={classes.expanded}>
              <AccordionSummary
                sx={{
                  padding: "0px",
                  "&.Mui-expanded": {
                    minHeight: 0,
                  },
                  "& .MuiAccordionSummary-content.Mui-expanded": {
                    margin: 0,
                  },
                }}>
                <Grid container className={classes.container}>
                  <Grid item xs={2}>
                    <CountryOrCryptoFlag name={imgSrc} size={32} />
                  </Grid>
                  <Grid item xs={5}>
                    <Grid container>
                      <Grid item xs={12}>
                        <Typography className={classes.title}>{side}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography className={classes.description}>{currency}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={5} textAlign={"right"}>
                    <Grid item xs={12}>
                      <Typography className={classes.title} translate="no">{units}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography className={classes.description}>Units</Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  padding: "15.5px 0px",
                  borderBottom: lines.length - 1 !== i ? "1px solid #d1d4d6" : "",
                }}>
                <Grid container>
                  <Grid item xs={2}></Grid>
                  <Grid item xs={10}></Grid>
                  <Grid item xs={2}></Grid>

                  <Grid item xs={5}>
                    <Grid container>
                      <Grid item xs={12}>
                        <Typography className={classes.description}>Unit Price</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={5} textAlign={"right"}>
                    <Grid item xs={12}>
                      <Typography className={classes.skinnyTitle} translate="no">{price}</Typography>
                    </Grid>
                  </Grid>

                  <Grid item xs={2}></Grid>

                  <Grid item xs={5}>
                    <Grid container>
                      <Grid item xs={12}>
                        <Typography className={classes.description}>Value</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={5} textAlign={"right"}>
                    <Grid item xs={12}>
                      <Typography className={classes.skinnyTitle} translate="no">{value}</Typography>
                    </Grid>
                  </Grid>

                  <Grid item xs={2}></Grid>

                  <Grid item xs={5}>
                    <Grid container>
                      <Grid item xs={12}>
                        <Typography className={classes.description}>Fees</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={5} textAlign={"right"}>
                    <Grid item xs={12}>
                      <Typography className={classes.skinnyTitle} translate="no">{fee}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </div>,
        );
      }
    }
    return renderResult;
  };

  return (
    <div>
      <Dialog fullScreen open={showTransactionDetails} onClose={toggleDetails}>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={toggleDetails} aria-label="close">
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Transaction Details
            </Typography>
          </Toolbar>
        </AppBar>

        <Container>
          {/* DATE */}
          <Grid item xs={12} container className={classes.dateContainer}>
            <Typography className={classes.dateText}>{date}</Typography>
          </Grid>

          {/* ICON */}
          <Grid container className={classes.topPanel}>
            <Grid item xs={2}>
              {iconSelector(details?.type)}
            </Grid>
            <Grid item xs={5} container>
              {/* TYPE */}
              <Grid item xs={12}>
                <Typography className={classes.title}>{details.type}</Typography>
              </Grid>

              {/* TIME */}
              <Grid item xs={12}>
                <Typography className={classes.time}>{details.time}</Typography>
              </Grid>
            </Grid>
            <Grid item xs container textAlign={"right"}>
              {/* VALUE */}
              <Grid item xs={12}>
                <Typography className={classes.title} translate="no">{details.amount}</Typography>
              </Grid>
            </Grid>
          </Grid>
          {renderLines()}

          <Collapse in={displayNote}>
            <Grid container>
              <Grid item xs={12}>
                <Grid container sx={{ marginBottom: "12px" }}>
                  <Grid item xs={12}>
                    <TextField
                      placeholder="Enter your note here, e.g. trade for the day"
                      fullWidth={true}
                      multiline
                      defaultValue={addNewNote}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        if (addNewNote !== event.target.value) {
                          setSaveNote(true);
                        }
                        setAddNewNote(event.target.value);
                      }}
                      rows={2}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Collapse>

          {hasNote && !saveNote ? (
            <Grid item xs>
              <SecondaryButton
                onClick={handleNote}
                endIcon={<AddNoteIcon height="20" width="20" color="#fff" />}>
                See note
              </SecondaryButton>
            </Grid>
          ) : (
            <Grid item xs>
              <SecondaryButton
                onClick={handleNote}
                endIcon={<AddNoteIcon height="20" width="20" color="#fff" />}>
                {displayNote ? "Save Note" : "Add Note"}
              </SecondaryButton>
            </Grid>
          )}

          <Snackbar
            open={notificationOpen}
            autoHideDuration={6000}
            onClose={handleNotificationClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
            <Alert onClose={handleNotificationClose} severity="success" sx={{ width: "100%" }}>
              Note successfully saved!
            </Alert>
          </Snackbar>
        </Container>
      </Dialog>
    </div>
  );
}
